import { useEffect, useState } from "react";
import { getRoleUiForPath } from "../../access/RoleUiCatalogue";
import { Button } from "@akin2unde/flowui-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { RoutePath } from "../../constants/RoutePath";
import { Layout } from "./Layout";
import WarehousePicker from "../../warehouse/WarehousePicker";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import TenantBrand from "../../components/TenantBrand/TenantBrand";
import "./LayoutCss.css";

const links: Array<{
  label: string;
  path: RoutePath;
  icon: string;
  permission?: string;
}> = [
  {
    label: "Dashboard",
    path: RoutePath.Dashboard,
    icon: "fa-chart-line",
    permission: "Dashboard.View",
  },
  { label: "Units of measurement", path: RoutePath.Units, icon: "fa-ruler", permission: "Unit.View" },
  {
    label: "Products",
    path: RoutePath.Products,
    icon: "fa-box",
    permission: "Product.View",
  },
  {
    label: "Categories",
    path: RoutePath.Categories,
    icon: "fa-tags",
    permission: "Category.View",
  },
  {
    label: "Customers",
    path: RoutePath.Customers,
    icon: "fa-users",
    permission: "Customer.View",
  },
  {
    label: "Suppliers",
    path: RoutePath.Suppliers,
    icon: "fa-truck-field",
    permission: "Supplier.View",
  },
  {
    label: "Purchases",
    path: RoutePath.Purchases,
    icon: "fa-file-invoice",
    permission: "Purchase.View",
  },
  {
    label: "Stock counts",
    path: RoutePath.InventoryCounts,
    icon: "fa-clipboard-check",
    permission: "Inventory.Manage",
  },
  {
    label: "Sales",
    path: RoutePath.Sales,
    icon: "fa-receipt",
    permission: "Sale.View",
  },
  {
    label: "Income",
    path: RoutePath.Income,
    icon: "fa-arrow-trend-up",
    permission: "Income.View",
  },
  {
    label: "Expenses",
    path: RoutePath.Expenses,
    icon: "fa-arrow-trend-down",
    permission: "Expense.View",
  },
  {
    label: "Orders",
    path: RoutePath.Orders,
    icon: "fa-cart-shopping",
    permission: "Order.View",
  },
  {
    label: "Stock transfers",
    path: RoutePath.InventoryTransfers,
    icon: "fa-truck-ramp-box",
    permission: "Inventory.Manage",
  },
  {
    label: "Stock",
    path: RoutePath.Inventory,
    icon: "fa-warehouse",
    permission: "Inventory.View",
  },
  // {
  //   label: "Order history",
  //   path: RoutePath.OrderHistory,
  //   icon: "fa-clock-rotate-left",
  //   permission: "Order.View",
  // },
  {
    label: "Warehouses",
    path: RoutePath.Warehouses,
    icon: "fa-building",
    permission: "Warehouse.View",
  },
  {
    label: "Stock movement",
    path: RoutePath.InventoryMovements,
    icon: "fa-right-left",
    permission: "Inventory.Manage",
  },
  {
    label: "Daily sales",
    path: RoutePath.DailySalesSummary,
    icon: "fa-chart-column",
    permission: "Report.View",
  },
  {
    label: "Notification settings",
    path: RoutePath.NotificationSettings,
    icon: "fa-bell",
    permission: "Notification.Manage",
  },
  {
    label: "Notification logs",
    path: RoutePath.NotificationLogs,
    icon: "fa-envelope",
    permission: "Notification.View",
  },
  {
    label: "SMTP settings",
    path: RoutePath.SmtpSettings,
    icon: "fa-server",
    permission: "Tenant.Manage",
  },
  {
    label: "Users",
    path: RoutePath.Users,
    icon: "fa-user-gear",
    permission: "User.View",
  },
  {
    label: "Roles",
    path: RoutePath.Roles,
    icon: "fa-user-shield",
    permission: "Role.View",
  },
  {
    label: "Permissions",
    path: RoutePath.Permissions,
    icon: "fa-key",
    permission: "Permission.View",
  },
  {
    label: "Banners",
    path: RoutePath.Banners,
    icon: "fa-image",
    permission: "Tenant.Manage",
  },
  {
    label: "Tenant",
    path: RoutePath.Tenant,
    icon: "fa-building-circle-check",
    permission: "Tenant.Manage",
  },
];

const menuGroups = [
 {label:"Catalogue",icon:"fa-box",paths:[RoutePath.Products,RoutePath.Categories,RoutePath.Units]},
 {label:"Sales",icon:"fa-receipt",paths:[RoutePath.Sales,RoutePath.Orders,RoutePath.Customers]},
 {label:"Purchasing",icon:"fa-file-invoice",paths:[RoutePath.Purchases,RoutePath.Suppliers]},
 {label:"Stock",icon:"fa-warehouse",paths:[RoutePath.Inventory,RoutePath.InventoryCounts,RoutePath.InventoryMovements,RoutePath.InventoryTransfers,RoutePath.Warehouses]},
 {label:"Finance",icon:"fa-coins",paths:[RoutePath.Income,RoutePath.Expenses]},
 {label:"Reports",icon:"fa-chart-column",paths:[RoutePath.DailySalesSummary]},
 {label:"Settings",icon:"fa-gear",paths:[RoutePath.Banners,RoutePath.Users,RoutePath.Roles,RoutePath.Permissions,RoutePath.Tenant,RoutePath.NotificationSettings,RoutePath.NotificationLogs,RoutePath.SmtpSettings]},
];

export default function LayoutHTML() {
  const { state, openSidebar, closeSidebar } = Layout();
  const { state: loggedInUser, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { Warehouse } = useWarehouse();

  const [openGroups,setOpenGroups]=useState<string[]>(()=>menuGroups.filter(g=>g.paths.includes(location.pathname as RoutePath)).map(g=>g.label));
  useEffect(()=>{const group=menuGroups.find(g=>g.paths.includes(location.pathname as RoutePath));if(group)setOpenGroups(current=>current.includes(group.label)?current:[...current,group.label]);},[location.pathname]);
  const visibleLinks=links.filter(link=>{const ui=getRoleUiForPath(link.path);return !link.permission||hasPermission(loggedInUser.RoleUis!==undefined&&ui?`${ui.Code}.View`:link.permission);});
  const renderLink=(link:typeof links[number])=><NavLink key={link.path} to={link.path} end onClick={()=>{closeSidebar();if(location.pathname===link.path)window.dispatchEvent(new CustomEvent("pantrystack:navigation-reselect"));}} className={({isActive})=>`layout-nav-link ${isActive?"layout-nav-link-active":""}`}><i className={`fa-solid ${link.icon}`} aria-hidden="true"/><span>{link.label}</span></NavLink>;

  const signOut = (): void => {
    logout();
    navigate(RoutePath.Login, { replace: true });
  };

  return (
    <div className="layout-root">
      {state.isSidebarOpen && (
        <button
          className="layout-backdrop"
          type="button"
          aria-label="Close navigation"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`layout-sidebar ${
          state.isSidebarOpen ? "layout-sidebar-open" : ""
        }`}
      >
        <div className="layout-logo">
          <TenantBrand Inverse />
        </div>
        <nav className="layout-nav" aria-label="Main navigation">
          {visibleLinks.filter(link=>link.path===RoutePath.Dashboard).map(renderLink)}
          {menuGroups.map(group=>{
            const children=group.paths.flatMap(path=>visibleLinks.filter(link=>link.path===path));
            if(!children.length) return null;
            return <div className="layout-nav-group" key={group.label}>
              <button type="button" className={`layout-group-toggle ${group.paths.includes(location.pathname as RoutePath)?"layout-group-current":""}`} aria-expanded={openGroups.includes(group.label)} onClick={()=>setOpenGroups(current=>current.includes(group.label)?current.filter(x=>x!==group.label):[...current,group.label])}>
                <i className={`fa-solid ${group.icon}`} aria-hidden="true"/><span>{group.label}</span><i className={`fa-solid fa-chevron-${openGroups.includes(group.label)?"up":"down"}`} aria-hidden="true"/>
              </button>
              {openGroups.includes(group.label)&&<div className="layout-group-children">{children.map(renderLink)}</div>}
            </div>;
          })}
        </nav>
      </aside>

      <div className="layout-main">
        <header className="layout-header">
          <button
            type="button"
            className="layout-menu-button"
            onClick={openSidebar}
            aria-label="Open navigation"
          >
            <i className="fa-solid fa-bars" aria-hidden="true" />
          </button>

          <WarehousePicker />
          <div className="layout-user">
            <div>
              <strong>{loggedInUser.User?.Name ?? "User"}</strong>
              <span>{loggedInUser.User?.MailAddress}</span>
            </div>
            <Button variant="outline" onClick={signOut} aria-label="Sign out">
              <i
                className="fa-solid fa-arrow-right-from-bracket"
                aria-hidden="true"
              />
              <span className="layout-signout-label">Sign out</span>
            </Button>
          </div>
        </header>

        <div className="layout-content">
          <Outlet key={Warehouse?.Code ?? "no-warehouse"} />
        </div>
      </div>
    </div>
  );
}
